import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Grid2x2, Plus } from 'lucide-react'
import { AuthService } from '@/services/core/auth'
import { LayoutsApi, Layout } from '@/services/sites/layoutsApi'
import { apiErrorDescription } from '@/services/core/apiError'
import { AppShell } from '../../components/AppShell'
import { LayoutTable } from '../../components/LayoutTable'
import { BulkActionBar } from '../../components/BulkActionBar'
import { StatusFilterButton } from '../../components/StatusFilter'
import { ListCard } from '../../components/ListCard'
import { ListCardHeader } from '../../components/ListCardHeader'
import { SearchInput } from '../../components/SearchInput'
import { Button } from '@/components/ui/button'
import { CardContent, CardIcon } from '@/components/ui/card'
import { usePersistedState } from '@/hooks/usePersistedState'
import { useUrlSearch } from '@/hooks/useUrlSearch'
import { walkAllPages, type ListSort } from '@/services/core/paging'
import { ListPagination } from '../../components/ListPagination'
import { PageSizeSelect } from '../../components/PageSizeSelect'

export function LayoutsPage() {
  const { t } = useTranslation()
  const hasToken = AuthService.isAuthenticated()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [statusFilter, setStatusFilter] = usePersistedState<'all' | 'ACTIVE' | 'INACTIVE'>('filters.layouts.status', 'ACTIVE')
  const [search, setSearch] = useUrlSearch()
  const [selection, setSelection] = useState<Layout[]>([])
  const [bulkLoading, setBulkLoading] = useState(false)

  const [page, setPage] = useState(1)
  const [sort, setSort] = useState<ListSort | null>(null)
  const [pageSize, setPageSize] = useState(25)

  // The list a page at a time, filtered and sorted as on screen.
  const fetchPage = (page: number, pageSize: number) =>
    LayoutsApi.getPage({ page, pageSize, search, status: statusFilter, sort })

  const { data, isLoading } = useQuery({
    queryKey: ['layouts', 'page', search, statusFilter, page, pageSize, sort],
    queryFn: () => fetchPage(page, pageSize),
    enabled: hasToken,
    staleTime: Infinity,
  })
  const layouts = data?.results ?? []
  const totalCount = data?.totalCount ?? 0

  /** Back to the first page whenever the rows underneath change. */
  const refilter = (apply: () => void) => {
    apply()
    setPage(1)
    setSelection([])
  }

  const handleSort = (next: ListSort | null) => {
    setSort(next)
    setPage(1)
  }

  const bulkSetStatus = async (state: 'ACTIVE' | 'INACTIVE') => {
    setBulkLoading(true)
    try {
      await Promise.all(
        selection.map((l) => LayoutsApi.update(l.id, { name: l.name, objectState: state }))
      )
      queryClient.invalidateQueries({ queryKey: ['layouts'] })
      toast.success(t('common.done'), {
        description: t(state === 'ACTIVE' ? 'layouts.doneDetail_activate' : 'layouts.doneDetail_deactivate', { count: selection.length }),
        duration: 3000,
      })
      setSelection([])
    } catch (e) {
      toast.error(t('common.error'), { description: apiErrorDescription(e), duration: 5000 })
    } finally {
      setBulkLoading(false)
    }
  }

  return (
    <AppShell crumbs={[{ label: t('layouts.title') }]}>
      <div className="ad-page">
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <h1>{t('layouts.title')}</h1>
            <p>{t('layouts.subtitle')}</p>
          </div>
          <div className="ad-page__actions">
            <Button
              onClick={() =>
                navigate(`/admin/layouts/${crypto.randomUUID().replace(/-/g, '').slice(0, 16)}?new=true`)
              }
            >
              <Plus className="size-4" /> {t('layouts.newLayout')}
            </Button>
          </div>
        </div>

        <ListCard fetchAll={() => walkAllPages(fetchPage)} tone="creative">
          <ListCardHeader
            title={
              <>
                <CardIcon icon={Grid2x2} />
                {t('layouts.title')}
              </>
            }
            count={totalCount}
            actions={
              <PageSizeSelect
                value={pageSize}
                onChange={(size) => refilter(() => setPageSize(size))}
              />
            }
            controls={
              <>
                <SearchInput value={search} onChange={(v) => refilter(() => setSearch(v))} />
                <StatusFilterButton
                  value={statusFilter}
                  onChange={(v) => refilter(() => setStatusFilter(v))}
                />
              </>
            }
          />

          <CardContent className="p-0">
            <BulkActionBar
              count={selection.length}
              onClear={() => setSelection([])}
              loading={bulkLoading}
              onActivate={() => bulkSetStatus('ACTIVE')}
              onDeactivate={() => bulkSetStatus('INACTIVE')}
            />
            <LayoutTable
              layouts={layouts}
              loading={isLoading}
              emptyMessage={
                hasToken ? t('layouts.noLayoutsFound') : t('common.signInToLoad')
              }
              selection={selection}
              onSelectionChange={setSelection}
              sort={sort}
              onSortChange={handleSort}
            />
          </CardContent>

          <ListPagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
            summary={t('layouts.layoutCount', { count: totalCount })}
          />
        </ListCard>
      </div>
    </AppShell>
  )
}
